/**
 * @class VariantSwatches
 * @extends {HTMLElement}
 * @description A custom element that manages a set of variant selection inputs (radio buttons).
 * When a variant is chosen, this component dispatches a 'VARIANT::UPDATED' custom event
 * with details about the selected variant, allowing other components on the page to react.
 */
class VariantSwatches extends HTMLElement {
  constructor() {
    super();

    this.elems = this.getElems();
    this.init();
  }

  init() {
    this.elems.radioButtons.forEach((radioButton) =>
      radioButton.addEventListener("change", this.handleChange.bind(this))
    );
  }

  /**
   * @method handleChange
   * @description Handles the 'change' event on the radio buttons. It extracts variant data
   * from the selected radio's `dataset` attributes and dispatches a custom event.
   * @param {Event} event - The native 'change' event object fired by the radio button.
   * @returns {void}
   */
  handleChange(event) {
    const {
      variantId,
      variantFeaturedMedia,
      variantDiscount,
      variantGraduatedPrices,
      variantIsPromotionalDiscount,
      variantHash,
      variantToken,
    } = event.target.dataset;
    const data = {
      variantId,
      variantFeaturedMedia,
      variantDiscount,
      variantGraduatedPrices,
      variantIsPromotionalDiscount,
      variantHash,
      variantToken,
    };
    const customEvent = new CustomEvent(
      VariantSwatches.events.VARIANT_UPDATED,
      {
        detail: data,
        bubbles: true,
      }
    );

    this.dispatchEvent(customEvent);
  }

  /**
   * @method getElems
   * @description Queries and returns essential DOM elements from within this component.
   * @returns {{radioButtons: HTMLInputElement[]}} An object containing the radio button elements.
   */
  getElems() {
    return {
      radioButtons: [...this.querySelectorAll('input[type="radio"]')],
    };
  }

  /**
   * @static
   * @property {object} events - A static object that centralizes the custom event names
   * used by this component. This avoids magic strings and makes event handling more robust.
   * @property {string} events.VARIANT_UPDATED - The event name fired when a variant is selected.
   */
  static events = {
    VARIANT_UPDATED: "VARIANT::UPDATED",
  };
}

customElements.define("variant-swatches", VariantSwatches);
